import type { SearchPagination, SearchResponse } from '@algolia/client-search';
import invariant from 'invariant';

import type { I18nLocale } from '#/i18n/types/I18nLocale';
import type {
  CmsApiAuthorSlug,
  CmsApiCategorySlug,
  CmsApiSubsiteId,
  CmsApiTagSlug,
} from '#/modules/cms/api/types/CmsApiTypedId';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';
import { algoliaQueryStringArrayFilter } from '#/utils/algolia/helpers/algoliaQueryStringArrayFilter';
import { algoliaQueryStringFilter } from '#/utils/algolia/helpers/algoliaQueryStringFilter';

import { postSearchAlgoliaGetIndex } from './postSearchAlgoliaGetIndex';
import type { PostSearchAlgoliaHit } from './types/PostSearchAlgoliaHit';

type Args = {
  query: string;
  pageIndex: number;
  subsiteId: CmsApiSubsiteId;
  includedSubsiteIds: CmsApiSubsiteId[];
  locale: I18nLocale;
  categorySlugs: CmsApiCategorySlug[];
  tagSlug: CmsApiTagSlug | undefined;
  authorSlug: CmsApiAuthorSlug | undefined;
  isUsingAlgoliaMock: boolean;
};

export function postSearchAlgoliaFetch({
  query,
  pageIndex,
  subsiteId,
  includedSubsiteIds,
  locale,
  categorySlugs,
  tagSlug,
  authorSlug,
  isUsingAlgoliaMock,
}: Args): Promise<
  SearchResponse<PostSearchAlgoliaHit> & Required<SearchPagination>
> {
  let indexName = postSearchAlgoliaGetIndex();
  invariant(typeof indexName === 'string', 'Post search index not defined');

  // use replica that's sorted differently when query is provided by user input
  if (query) indexName = `${indexName}-relevant`;

  const filters = [
    algoliaQueryStringFilter('pageType', 'POST'),
    algoliaQueryStringFilter('locale', locale),
    algoliaQueryStringArrayFilter('categories', categorySlugs),
    algoliaQueryStringFilter('author.slug', authorSlug),
    algoliaQueryStringFilter('tags.slug', tagSlug),
  ]
    .filter(Boolean)
    .join(' AND ');

  return algoliaSearch<PostSearchAlgoliaHit>({
    config: getSearchAlgoliaDefaultConfig(),
    query,
    indexName,
    options: {
      hitsPerPage: 20,
      page: pageIndex,
      facets: ['*', 'subsiteId', 'pageType', 'categoryIds', 'locale'],
      facetFilters: [
        [...includedSubsiteIds, subsiteId].map((id) => `subsiteId:${id}`),
      ],
      filters,
      attributesToSnippet: ['description:20'],
      attributesToRetrieve: ['*'],
      // TODO: list attributes,
      removeStopWords: true,
      ignorePlurals: true,
      advancedSyntax: true,
      queryLanguages: [locale],
    },
    isUsingAlgoliaMock,
  });
}
